import { el, clear, isInteracting } from "../dom.js";
import { Connection } from "../relay.js";
import { bottomNav } from "../nav.js";
import { projectTitle } from "../topbar.js";
import { notificationBell } from "../bell.js";
import { pendingCount } from "./notifications.js";
import type { DaemonEntry } from "../store.js";
import type { Attention, BigTask } from "../types.js";
import { activeWorkItems, buildWorkItems, renderWorkGroups } from "./work-groups.js";

const REFETCH_DEBOUNCE_MS = 500;

export function renderWork(
  root: HTMLElement,
  entry: DaemonEntry,
  onSwitch: () => void,
  onFeed: () => void,
  onBacklog: () => void,
  onSettings: () => void,
  onNotifications: () => void,
): () => void {
  clear(root);

  const dot = el("span", { class: "status-dot" });
  const banner = el("div", {});
  const list = el("div", {}, [el("div", { class: "empty" }, ["Connecting…"])]);

  let bell = notificationBell({ count: 0, onClick: onNotifications });
  const updateBell = (count: number) => {
    const next = notificationBell({ count, onClick: onNotifications });
    bell.replaceWith(next);
    bell = next;
  };

  root.append(
    el("header", { class: "topbar" }, [
      projectTitle(dot, entry.name, "Work", onSwitch),
      bell,
    ]),
    banner,
    list,
    bottomNav({ active: null, onFeed, onBoard: onBacklog, onSettings }),
  );

  let conn: Connection | null = null;
  let stopped = false;
  let refetchTimer = 0;
  let reconnectDelay = 1000;

  const setOnline = (online: boolean) => {
    dot.className = `status-dot ${online ? "online" : "offline"}`;
  };

  const refetch = async () => {
    if (!conn) return;
    try {
      const [a, bigTasks] = await Promise.all([
        conn.api<Attention>("GET", "/api/attention"),
        conn.api<BigTask[]>("GET", "/api/bigtasks"),
      ]);
      updateBell(pendingCount(a));
      clear(list);
      list.append(renderWorkGroups(activeWorkItems(buildWorkItems(a.plans ?? [], bigTasks ?? [])), conn!.api.bind(conn!)));
      banner.replaceChildren();
    } catch (e) {
      banner.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
    }
  };

  const scheduleRefetch = () => {
    clearTimeout(refetchTimer);
    refetchTimer = window.setTimeout(() => {
      // Don't rebuild the list out from under someone reading or typing — retry
      // on the next tick so the update still lands once they're done.
      if (isInteracting(list)) return scheduleRefetch();
      void refetch();
    }, REFETCH_DEBOUNCE_MS);
  };

  const connect = async () => {
    if (stopped) return;
    banner.replaceChildren();
    const c = new Connection(entry, {
      onEvent: () => scheduleRefetch(),
      onClose: () => {
        setOnline(false);
        if (stopped) return;
        banner.replaceChildren(el("div", { class: "notice" }, ["Connection lost — reconnecting…"]));
        window.setTimeout(() => void connect(), reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
      },
    });
    try {
      await c.connect();
      conn = c;
      reconnectDelay = 1000;
      setOnline(true);
      await refetch();
    } catch (e) {
      setOnline(false);
      clear(list);
      list.append(el("div", { class: "empty" }, ["Daemon unreachable."]));
      banner.replaceChildren(
        el("div", { class: "error-banner" }, [(e as Error).message]),
        el("div", { class: "actions" }, [el("button", { onclick: () => void connect() }, ["Retry"])]),
      );
    }
  };
  void connect();

  return () => {
    stopped = true;
    clearTimeout(refetchTimer);
    conn?.close();
  };
}
